"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useState, type ReactNode } from "react";
import { useClientSidebar } from "./client-sidebar-context";
import { NavFlyout, type FlyoutItem } from "./nav-flyout";
import { icons } from "./nav-icons";
import { canShowClientNavItem, clientDashboardNavHasData } from "./client-nav-visibility";
import type { ClientPermissionKey } from "@/lib/auth/client-permissions";
import {
  AUDIT_LINKS,
  DEEP_RESEARCH_LINK,
  INTEGRATION_LINKS,
  STANDARD_VIEWS,
  type NavDestination,
} from "@/lib/nav/registry";

/**
 * The nav data lives in src/lib/nav/registry.ts so global search enumerates the
 * same destinations under the same gates. Icons stay here: the registry is pure
 * data (string keys) so it can be imported by logic and tests without React.
 */
const iconFor = (destination: NavDestination) => icons[destination.iconKey];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

interface SidebarNavProps {
  role: string;
  clientCount?: number;
  variant?: "desktop" | "mobile";
}

interface MobileNavGroupProps {
  navKey: string;
  label: string;
  icon?: ReactNode;
  items: FlyoutItem[];
  badgeCount?: number;
  tourAnchor?: string;
  onNavigate: () => void;
}

function MobileNavGroup({
  navKey,
  label,
  icon,
  items,
  badgeCount,
  tourAnchor,
  onNavigate,
}: MobileNavGroupProps) {
  const containsActive = items.some((item) => item.active);
  const [open, setOpen] = useState(containsActive);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`mobile-nav-group-${navKey}`}
        data-tour={tourAnchor}
        onClick={() => setOpen((value) => !value)}
        className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
          containsActive
            ? "bg-spark-green/[.13] font-bold text-[#4AE293]"
            : "font-medium text-[#C3CAD3] hover:bg-white/[.06] hover:text-white"
        }`}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {(badgeCount ?? 0) > 0 && (
          <span className="rounded-full bg-spark-green px-1.5 py-px text-[10px] font-bold text-spark-ink tnum">
            {badgeCount}
          </span>
        )}
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {open && (
        <div id={`mobile-nav-group-${navKey}`} className="ml-5 mt-1 space-y-0.5 border-l border-white/[.1] pl-3">
          {items.map((item) => (
            <div key={item.key}>
              {item.dividerBefore && <div className="mx-2 my-1.5 border-t border-white/[.08]" />}
              <Link
                href={item.href}
                aria-current={item.active ? "page" : undefined}
                title={item.disabled ? "No data yet — connect integrations to populate" : undefined}
                onClick={onNavigate}
                className={`flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  item.active
                    ? "bg-spark-green/[.13] font-bold text-[#4AE293]"
                    : item.disabled
                      ? "font-medium text-[#7D8693] opacity-70"
                      : "font-medium text-[#AAB2BD] hover:bg-white/[.06] hover:text-white"
                }`}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">{item.icon}</span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {(item.badgeCount ?? 0) > 0 && (
                  <span className="rounded-full bg-spark-green px-1.5 py-px text-[10px] font-bold text-spark-ink tnum">
                    {item.badgeCount}
                  </span>
                )}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SidebarNav({ role, clientCount, variant = "desktop" }: SidebarNavProps) {
  const { client, collapsed, demoMode, setMobileNavOpen } = useClientSidebar();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const mobile = variant === "mobile";
  const navCollapsed = !mobile && collapsed;
  const closeMobileNav = () => setMobileNavOpen(false);

  const clientId = client?.clientId ?? "";

  const isAdminOrManager = role === "developer" || role === "admin" || role === "manager";
  const canManageConnections = isAdminOrManager || Boolean(
    client?.permissions && canShowClientNavItem("integrations.manage_connections", {
      demoMode,
      isStaff: isAdminOrManager,
      permissions: client.permissions,
    }),
  );

  /** Provider gate + staff gate - the one visibility rule for tool links. */
  const linkVisible = (l: NavDestination, providers: string[]) =>
    (!l.provider || providers.includes(l.provider)) && (!l.staffOnly || isAdminOrManager);

  // Collapsed is the icon rail: fixed squares, centered, no labels. Expanded is
  // a full-width row with the label beside the icon.
  const linkBase = navCollapsed
    ? "mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium text-[#AAB2BD] hover:bg-white/[.06] hover:text-white transition-colors"
    : "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#AAB2BD] hover:bg-white/[.05] hover:text-white transition-colors";
  const linkActive = navCollapsed
    ? "mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-spark-green/[.13] text-sm font-bold text-[#4AE293] transition-colors"
    : "flex w-full items-center gap-2 rounded-md bg-spark-green/[.13] px-3 py-2 text-sm font-bold text-[#4AE293] transition-colors";

  // Smaller treatment for the "back to all companies" row above the switcher.
  const backBase = navCollapsed
    ? linkBase
    : "flex w-full items-center gap-1 rounded-md px-3 py-2 text-xs font-medium text-[#AAB2BD] hover:bg-white/[.05] hover:text-white transition-colors";

  // Leading dot marking the active item (expanded mode only)
  const dot = (
    <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-spark-green" />
  );

  /** One rail row: a plain link (hover shows a label card on the icon rail). */
  function railLink(
    navKey: string,
    label: string,
    href: string,
    icon: ReactNode,
    active: boolean,
    opts?: { tourAnchor?: string; badgeCount?: number; triggerBase?: string },
  ) {
    if (mobile) {
      return (
        <Link
          key={navKey}
          href={href}
          aria-current={active ? "page" : undefined}
          data-tour={opts?.tourAnchor}
          onClick={closeMobileNav}
          className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
            active
              ? "bg-spark-green/[.13] font-bold text-[#4AE293]"
              : "font-medium text-[#C3CAD3] hover:bg-white/[.06] hover:text-white"
          }`}
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">{icon}</span>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {(opts?.badgeCount ?? 0) > 0 && (
            <span className="rounded-full bg-spark-green px-1.5 py-px text-[10px] font-bold text-spark-ink tnum">
              {opts?.badgeCount}
            </span>
          )}
        </Link>
      );
    }
    return (
      <NavFlyout
        key={navKey}
        navKey={navKey}
        label={label}
        href={href}
        icon={icon}
        items={[]}
        collapsed={navCollapsed}
        containsActive={active}
        triggerBase={opts?.triggerBase ?? linkBase}
        triggerHighlight={linkActive}
        dot={dot}
        badgeCount={opts?.badgeCount}
        tourAnchor={opts?.tourAnchor}
      />
    );
  }

  /**
   * One rail row for a group: renders the flyout, collapses to a plain link
   * when only one item survives its gates, and nothing when none do. Clicking
   * the trigger navigates to the group's default page — the first item that
   * isn't dimmed (Publishing → Calendar, Dashboards → Overview, Integrations/
   * Audits → the highest connected tool) — while hover still opens the flyout.
   */
  function railGroup(
    navKey: string,
    label: string,
    icon: ReactNode,
    items: FlyoutItem[],
    opts?: { headerCountLabel?: string; badgeCount?: number; tourAnchor?: string },
  ) {
    if (items.length === 0) return null;
    if (items.length === 1) {
      const only = items[0];
      return railLink(navKey, only.label, only.href, only.icon ?? icon, only.active, {
        tourAnchor: opts?.tourAnchor,
        badgeCount: only.badgeCount,
      });
    }
    if (mobile) {
      return (
        <MobileNavGroup
          key={`${navKey}:${items.some((item) => item.active)}`}
          navKey={navKey}
          label={label}
          icon={icon}
          items={items}
          badgeCount={opts?.badgeCount}
          tourAnchor={opts?.tourAnchor}
          onNavigate={closeMobileNav}
        />
      );
    }
    const defaultItem = items.find((item) => !item.disabled) ?? items[0];
    return (
      <NavFlyout
        key={navKey}
        navKey={navKey}
        label={label}
        icon={icon}
        items={items}
        href={defaultItem.href}
        collapsed={navCollapsed}
        containsActive={items.some((i) => i.active)}
        triggerBase={linkBase}
        triggerHighlight={linkActive}
        dot={dot}
        headerCountLabel={opts?.headerCountLabel}
        badgeCount={opts?.badgeCount}
        tourAnchor={opts?.tourAnchor}
      />
    );
  }

  const divider = <div className="mx-3 my-2 border-t border-white/[.08]" />;

  // Flash mitigation: if pathname looks like a client page but context isn't set yet, show skeleton
  const isClientPath = /^\/clients\/[^/]+/.test(pathname);
  if (isClientPath && !client) {
    // Match the shape of whichever nav is about to replace it: rail squares
    // collapsed, full-width rows expanded.
    return navCollapsed ? (
      <nav className="flex flex-1 flex-col items-center space-y-1.5">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="h-9 w-9 animate-pulse rounded-lg bg-white/[.08]" />
        ))}
      </nav>
    ) : (
      <nav className="flex-1 space-y-2 px-1">
        <div className="h-4 w-20 animate-pulse rounded bg-white/[.08]" />
        <div className="h-8 w-full animate-pulse rounded bg-white/[.08]" />
        <div className="h-4 w-24 animate-pulse rounded bg-white/[.08] mt-4" />
        <div className="h-6 w-full animate-pulse rounded bg-white/[.08]" />
        <div className="h-6 w-full animate-pulse rounded bg-white/[.08]" />
        <div className="h-6 w-full animate-pulse rounded bg-white/[.08]" />
      </nav>
    );
  }

  // Global mode
  const showDashboardLink = isAdminOrManager || (clientCount ?? 0) > 1;
  const dashboardLabel = isAdminOrManager ? "Agency Dashboard" : "My Companies";

  if (!client) {
    const agencyToolItems: FlyoutItem[] = isAdminOrManager
      ? [
          { key: "websites", label: "Website List", href: "/agency/websites", icon: icons.globe, active: pathname === "/agency/websites" },
          { key: "uptime", label: "Uptime", href: "/agency/uptime", icon: icons.activity, active: pathname === "/agency/uptime" },
          { key: "publishing-approvals", label: "Publishing Approvals", href: "/agency/social-approvals", icon: icons.send, active: pathname === "/agency/social-approvals" },
          { key: "tours", label: "Guided Tours", href: "/agency/tours", icon: icons.map, active: pathname === "/agency/tours" },
          ...(role === "developer" || role === "admin"
            ? [{ key: "deep-research", label: "Deep Research", href: "/agency/deep-research", icon: icons.sparkles, active: pathname === "/agency/deep-research", dividerBefore: true }]
            : []),
          ...(role === "developer"
            ? [{ key: "costs", label: "API Costs", href: "/agency/costs", icon: icons.barChart, active: pathname === "/agency/costs" }]
            : []),
          { key: "docs", label: "Documentation", href: "/docs", icon: icons.book, active: pathname.startsWith("/docs"), dividerBefore: true },
          { key: "changelog", label: "Changelog", href: "/agency/changelog", icon: icons.fileText, active: pathname === "/agency/changelog" },
        ]
      : [];

    const adminItems: FlyoutItem[] = isAdminOrManager
      ? [
          { key: "users", label: "Users", href: "/admin/users", icon: icons.users, active: pathname.startsWith("/admin/users") },
          { key: "licenses", label: "Licenses", href: "/admin/licenses", icon: icons.gear, active: pathname.startsWith("/admin/licenses") },
          ...(role === "developer"
            ? [{ key: "dev-tools", label: "Developer Tools", href: "/admin/tools", icon: icons.gear, active: pathname.startsWith("/admin/tools"), dividerBefore: true }]
            : []),
        ]
      : [];

    return (
      <nav data-tour="sidebar-nav" className="flex flex-1 flex-col space-y-0.5 overflow-y-auto">
        {showDashboardLink && railLink("home", dashboardLabel, "/", icons.grid, pathname === "/")}

        {isAdminOrManager && (
          <>
            {!navCollapsed && (
              <p className="px-3 pt-5 pb-1 text-[10px] font-bold uppercase tracking-[.1em] text-[#7D8693]">
                Agency Tools
              </p>
            )}
            {navCollapsed && divider}
            {railGroup("agency-tools", "Agency Tools", icons.layers, agencyToolItems)}
            {railGroup("admin", "Admin", icons.gear, adminItems)}
          </>
        )}
      </nav>
    );
  }

  // Client mode (clientId is already derived at the top of the component)
  const { providers, customDashboards, hiddenViews } = client;
  const canShow = (permission: ClientPermissionKey, hasContent = true) => canShowClientNavItem(permission, {
    demoMode,
    hasContent,
    isStaff: isAdminOrManager,
    permissions: client.permissions,
  });

  const range = searchParams.get("range");
  const compare = searchParams.get("compare");

  function buildHref(path: string): string {
    const base = `/clients/${clientId}/${path}`;
    const params = new URLSearchParams();
    if (range) params.set("range", range);
    if (compare) params.set("compare", compare);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  function isActive(path: string): boolean {
    const full = `/clients/${clientId}/${path}`;
    return pathname === full || pathname.startsWith(full + "/");
  }

  const isOverviewActive =
    pathname === `/clients/${clientId}/setup` ||
    pathname === `/clients/${clientId}` ||
    pathname === `/clients/${clientId}/edit`;

  const hiddenViewSet = new Set(hiddenViews);
  const providerSet = new Set(providers);

  /* --- Pulse: the weekly board, plus the staff-only snapshot archive --- */
  const pulseItems: FlyoutItem[] = [
    ...(canShow("pulse.view", client.contentAvailability.pulse) && !hiddenViewSet.has("pulse")
      ? [{
          key: "pulse",
          label: "Pulse",
          href: buildHref("pulse"),
          icon: icons.signal,
          active: pathname === `/clients/${clientId}/pulse`,
        }]
      : []),
    ...(isAdminOrManager
      ? [{
          key: "pulse-history",
          label: "Pulse history",
          href: `/clients/${clientId}/pulse/history`,
          icon: icons.bell,
          active: isActive("pulse/history"),
          dividerBefore: true,
        }]
      : []),
  ];

  /* --- Publishing: shared calendar + approvals, then the per-module surfaces --- */
  const socialVisible =
    client.socialEnabled &&
    canShow("social.view", client.contentAvailability.social) &&
    !hiddenViewSet.has("social_publishing");
  const sitemapsVisible =
    client.sitemapsEnabled &&
    canShow("sitemaps.view", client.contentAvailability.sitemaps) &&
    !hiddenViewSet.has("sitemap_planner");
  const mediaVisible =
    (client.socialEnabled || client.sitemapsEnabled) &&
    canShow("media.view", client.contentAvailability.media) &&
    !hiddenViewSet.has("media_library");
  const socialConnected = ["meta", "instagram", "linkedin"].some((p) => providers.includes(p));

  const publishingItems: FlyoutItem[] =
    !socialVisible && !sitemapsVisible && !mediaVisible
      ? []
      : [
          ...(socialVisible || sitemapsVisible
            ? [{
                key: "publishing/calendar",
                label: "Calendar",
                href: buildHref("publishing/calendar"),
                icon: icons.calendar,
                active: isActive("publishing/calendar"),
              }]
            : []),
          // The list-mode counterpart to the calendar. Replaced Gallery
          // (2026-07-25), which was this list filtered to posts in cards mode,
          // so it now covers pages too and shows whenever either module is on.
          ...(socialVisible || sitemapsVisible
            ? [{
                key: "publishing/content",
                label: "Content",
                href: buildHref("publishing/content"),
                icon: icons.megaphone,
                // Post detail and the composer live under /publishing/social.
                active:
                  isActive("publishing/content") ||
                  isActive("publishing/gallery") ||
                  isActive("publishing/social"),
                // Only a social-only viewer needs a connected channel; a
                // sitemaps viewer has page rows regardless.
                disabled: socialVisible && !sitemapsVisible && !socialConnected,
              }]
            : []),
          ...(socialVisible || sitemapsVisible
            ? [{
                key: "publishing/approvals",
                label: "Approvals",
                href: buildHref("publishing/approvals"),
                icon: icons.checkCircle,
                active: isActive("publishing/approvals"),
                badgeCount: client.publishingPendingCount ?? 0,
              }]
            : []),
          ...(sitemapsVisible
            ? [{
                key: "publishing/sitemaps",
                label: "Sitemaps",
                href: buildHref("publishing/sitemaps"),
                icon: icons.shareNodes,
                active: isActive("publishing/sitemaps"),
              }]
            : []),
          ...(mediaVisible
            ? [{
                key: "publishing/media",
                label: "Media",
                href: buildHref("publishing/media"),
                icon: icons.photo,
                active: isActive("publishing/media"),
              }]
            : []),
        ];

  /* --- Dashboards: standard views + custom dashboards + New (staff) --- */
  // Client users do not see empty dashboards. Staff retain the dimmed setup
  // links so they can configure companies before data is connected.
  const viewItems: FlyoutItem[] = STANDARD_VIEWS
    .map((v) => {
      const active = pathname === `/clients/${clientId}/${v.path}`;
      const hasData = demoMode || clientDashboardNavHasData(v.key, providerSet);
      return { v, active, hasData };
    })
    .filter(({ v, hasData }) => canShow(v.permission, hasData) && !hiddenViewSet.has(v.key))
    .sort((a, b) => Number(b.hasData) - Number(a.hasData))
    .map(({ v, active, hasData }) => ({
      key: v.key,
      label: v.label,
      href: buildHref(v.path),
      icon: iconFor(v),
      active,
      disabled: !hasData && !active,
    }));

  const dashboardItems: FlyoutItem[] = [
    ...viewItems,
    ...customDashboards.map((d, i) => ({
      key: `custom:${d.id}`,
      label: d.name,
      href: buildHref(`custom/${d.id}`),
      icon: icons.grid,
      active: isActive(`custom/${d.id}`),
      dividerBefore: i === 0,
    })),
    ...(isAdminOrManager
      ? [
          {
            key: "new-dashboard",
            label: "New dashboard",
            href: `/clients/${clientId}/custom/new`,
            icon: icons.plus,
            active: false,
            dividerBefore: true,
          },
        ]
      : []),
  ];

  /* --- Integrations + Audits & Tools (Deep Research folded in) --- */
  const toItems = (links: NavDestination[]): FlyoutItem[] =>
    links.map((l) => ({
      key: l.path,
      label: l.label,
      href: buildHref(l.path),
      icon: iconFor(l),
      active: isActive(l.path),
    }));

  const integrationItems = toItems(INTEGRATION_LINKS.filter((l) => linkVisible(l, providers)));
  const auditItems: FlyoutItem[] = [
    ...toItems(AUDIT_LINKS.filter((l) => linkVisible(l, providers))),
    ...(linkVisible(DEEP_RESEARCH_LINK, providers)
      ? [{
          key: DEEP_RESEARCH_LINK.path,
          label: DEEP_RESEARCH_LINK.label,
          href: buildHref(DEEP_RESEARCH_LINK.path),
          icon: iconFor(DEEP_RESEARCH_LINK),
          active: isActive(DEEP_RESEARCH_LINK.path),
          dividerBefore: true,
        }]
      : []),
  ];

  /* --- Setup (staff) or Manage integrations (permitted client admins) --- */
  const setupItems: FlyoutItem[] = isAdminOrManager
    ? [{
        key: "setup",
        label: "Setup",
        href: `/clients/${clientId}/setup`,
        icon: icons.gear,
        active: isOverviewActive,
      },
      {
        key: "ai-context",
        label: "AI Context Notes",
        href: `/clients/${clientId}/setup/ai-context`,
        icon: icons.sparkles,
        active: isActive("setup/ai-context"),
        dividerBefore: true,
      }]
    : canManageConnections
      ? [{
          key: "manage-integrations",
          label: "Manage integrations",
          href: `/clients/${clientId}/integrations/new`,
          icon: icons.gear,
          active: isActive("integrations/new"),
        }]
      : [];

  const reportsVisible =
    canShow("reports.view", client.contentAvailability.reports) && !hiddenViewSet.has("reports");

  return (
    <nav data-tour="sidebar-nav" className="flex flex-1 flex-col space-y-0.5 overflow-y-auto">
      {/* Back link — admin/manager or multi-client users */}
      {showDashboardLink &&
        railLink(
          "back",
          isAdminOrManager ? "All Companies" : "My Companies",
          "/",
          icons.arrowLeft,
          false,
          { triggerBase: backBase },
        )}

      {/* Pulse — the board, with the snapshot archive folded in for staff */}
      {railGroup("pulse", "Pulse", icons.signal, pulseItems, { tourAnchor: "pulse-nav" })}

      {/* Campaigns */}
      {canShow("campaigns.view", client.contentAvailability.campaigns) &&
        !hiddenViewSet.has("campaign_tracker") &&
        railLink("campaigns", "Campaigns", buildHref("campaigns"), icons.trendingUp, isActive("campaigns"))}

      {/* Publishing — badge = items awaiting the signed-in user's approval */}
      {railGroup("publishing", "Publishing", icons.send, publishingItems, {
        badgeCount: client.publishingPendingCount ?? 0,
      })}

      {/* Dashboards */}
      {railGroup("dashboards", "Dashboards", icons.barChart, dashboardItems)}

      {/* Browse groups */}
      {(integrationItems.length > 0 || auditItems.length > 0) && (
        <>
          {navCollapsed && divider}
          {railGroup("integrations", "Integrations", icons.layers, integrationItems, {
            headerCountLabel: "active",
          })}
          {railGroup("audits", "Audits & Tools", icons.gauge, auditItems, {
            headerCountLabel: "active",
          })}
        </>
      )}

      {/* Divider + Reports */}
      {reportsVisible && (
        <>
          {divider}
          {railLink("reports", "Reports", `/clients/${clientId}/reports`, icons.folder, isActive("reports"))}
        </>
      )}

      {railGroup("setup", "Setup", icons.gear, setupItems)}
    </nav>
  );
}
